import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSettingsOpeningHoursQueryKey,
  useGetSettingsOpeningHours,
  usePutSettingsOpeningHours,
} from '@agos/api-client/platform'
import { T, formatCount, tt, useAuth } from '@agos/shared'
import { ConfirmDialog, PageHeader, Spinner, StatusBadge, cn, useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Clock,
  Copy,
  Megaphone,
  RotateCcw,
  Save,
  ShieldAlert,
  Timer,
} from 'lucide-react'
import { useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  changeKindLabel,
  copyMondayAcross,
  customerFacingMessage,
  customerFacingState,
  dayLabel,
  dayProblem,
  dayProblemMessage,
  effectiveCutoff,
  formOf,
  orderingMinutes,
  payloadOf,
  problems,
  resetChanges,
  resetForm,
  summarise,
  type Day,
  type HoursForm,
  type OpeningHours,
} from './opening-hours'

/**
 * `/opening-hours` — when the restaurant is open, and until when it takes orders.
 *
 * **FB-133.** Legacy awaits the PUT without checking `res.ok`, writes the browser copy anyway, and shows
 * a tick — so a refused save leaves a local copy of hours the server never took, which
 * `loadOpeningHours()` then prefers on the next load. There is no local copy here.
 *
 * **FB-134.** Its *reset* button writes hard-coded defaults that are not this restaurant's hours: no
 * `orderCutoff` on any day where the live data has 21:47 on all seven, and Sunday closed where it trades
 * 12:00–22:00. One click extends order-taking by thirteen minutes and shuts a trading day, silently. The
 * button stays — it is useful — but it now lists every change first.
 *
 * The write is gated on `opening_hours_edit` **or** `main_admin`, not on a role, so a waiter holding the
 * flag may edit and an admin without it may not.
 *
 * Ported from legacy `pages/opening-hours.tsx` (376 lines).
 */

export function OpeningHoursPage() {
  const { data, isLoading } = useGetSettingsOpeningHours()

  if (isLoading || data === undefined) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return <HoursEditor hours={data} />
}

function HoursEditor({ hours }: { hours: OpeningHours }) {
  const { toast } = useToast()
  const { user, hasPermission } = useAuth()
  const queryClient = useQueryClient()

  const [form, setForm] = useState<HoursForm>(() => formOf(hours))
  const [confirmReset, setConfirmReset] = useState(false)

  const writable = mayWrite('opening-hours', user?.systemRole, hasPermission)
  const readOnly = readOnlyReason('opening-hours', user?.systemRole, hasPermission)
  const broken = problems(form)
  const summary = summarise(form)
  const state = customerFacingState(form)
  const pendingReset = resetChanges(form)

  const save = usePutSettingsOpeningHours({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetSettingsOpeningHoursQueryKey() })
        toast({ title: tt('Otváracie hodiny uložené') })
      },
      // FB-133: legacy shows the tick here and writes the browser copy anyway.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(caught, tt('Na serveri sa nič nezmenilo.')),
        }),
    },
  })

  const patchDay = (key: Day['key'], patch: Partial<Day>) =>
    setForm((current) => ({
      ...current,
      days: current.days.map((day) => (day.key === key ? { ...day, ...patch } : day)),
    }))

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Otváracie hodiny')}
        description={tt('Kedy je otvorené a dokedy sa prijímajú objednávky')}
        icon={<Clock className="w-5 h-5" />}
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {readOnly}
        </p>
      )}

      <div
        className={cn(
          'rounded-2xl border px-4 py-3 flex items-center gap-3',
          state === 'open' ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-red-500/40 bg-red-500/5',
        )}
        data-customer-state={state}
      >
        {state === 'open' ? (
          <Clock className="w-5 h-5 text-emerald-500 shrink-0" />
        ) : (
          <ShieldAlert className="w-5 h-5 text-red-500 shrink-0" />
        )}
        <div>
          <p className={cn('text-sm font-semibold', state === 'open' ? 'text-emerald-500' : 'text-red-500')}>
            {customerFacingMessage(state)}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatCount(summary.openDays)} <T>otvorených dní</T> ·{' '}
            {formatCount(summary.daysWithCutoff)} <T>s vlastným koncom príjmu objednávok</T>
          </p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            <T>Týždeň</T>
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-copy-monday
              disabled={!writable}
              onClick={() => setForm(copyMondayAcross(form))}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-xs font-semibold disabled:opacity-50"
            >
              <Copy className="w-3.5 h-3.5" /> <T>Skopírovať pondelok</T>
            </button>
            <button
              type="button"
              data-reset
              disabled={!writable}
              onClick={() => setConfirmReset(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-xs font-semibold disabled:opacity-50"
            >
              <RotateCcw className="w-3.5 h-3.5" /> <T>Predvolené hodiny</T>
            </button>
          </div>
        </div>

        <div className="divide-y divide-border">
          {form.days.map((day) => {
            const problem = dayProblem(day)
            const cutoff = effectiveCutoff(day)
            const minutes = orderingMinutes(day)
            return (
              <div key={day.key} className="px-5 py-4 space-y-2" data-day={day.key}>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={day.open}
                    aria-label={dayLabel(day.key)}
                    disabled={!writable}
                    data-day-open={day.open ? 'yes' : 'no'}
                    onClick={() => patchDay(day.key, { open: !day.open })}
                    className={cn(
                      'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
                      day.open ? 'bg-primary' : 'bg-muted',
                    )}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
                        day.open ? 'left-[22px]' : 'left-0.5',
                      )}
                    />
                  </button>
                  <p className="text-sm font-semibold w-24 shrink-0">{dayLabel(day.key)}</p>

                  {day.open ? (
                    <>
                      <input
                        type="time"
                        aria-label={`${dayLabel(day.key)} - ${tt('otvára')}`}
                        disabled={!writable}
                        value={day.from}
                        onChange={(event) => patchDay(day.key, { from: event.target.value })}
                        className={TIME_FIELD}
                      />
                      <span className="text-muted-foreground">–</span>
                      <input
                        type="time"
                        aria-label={`${dayLabel(day.key)} - ${tt('zatvára')}`}
                        disabled={!writable}
                        value={day.to}
                        onChange={(event) => patchDay(day.key, { to: event.target.value })}
                        className={TIME_FIELD}
                      />
                      <span className="flex items-center gap-1.5 ml-2">
                        <Timer className="w-3.5 h-3.5 text-muted-foreground" />
                        <input
                          type="time"
                          aria-label={`${dayLabel(day.key)} - ${tt('koniec príjmu objednávok')}`}
                          disabled={!writable}
                          value={day.orderCutoff ?? ''}
                          onChange={(event) =>
                            patchDay(day.key, {
                              orderCutoff: event.target.value === '' ? null : event.target.value,
                            })
                          }
                          className={TIME_FIELD}
                        />
                      </span>
                    </>
                  ) : (
                    <StatusBadge label={tt('Zatvorené')} color="zinc" />
                  )}
                </div>

                {day.open && (
                  <p className="text-[11px] text-muted-foreground pl-[3.5rem]" data-day-cutoff={cutoff.source}>
                    {/* A blank cutoff does not mean *no cutoff*; it means the closing time is one. */}
                    <T>Objednávky sa prijímajú do</T> <strong>{cutoff.time}</strong>{' '}
                    {cutoff.source === 'closing' ? (
                      <T>(zatváracia hodina - vlastný koniec nie je nastavený)</T>
                    ) : (
                      <T>(vlastný koniec príjmu)</T>
                    )}
                    {minutes !== null && minutes > 0 && (
                      <>
                        {' · '}
                        {formatCount(Math.floor(minutes / 60))} <T>h</T>{' '}
                        {formatCount(minutes % 60)} <T>min</T>
                      </>
                    )}
                  </p>
                )}

                {problem !== null && (
                  <p className="text-[11px] text-red-500 pl-[3.5rem]" data-day-problem={problem}>
                    {dayProblemMessage(problem)}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-primary" />
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            <T>Mimoriadne zatvorenie</T>
          </p>
        </div>
        <div className="px-5 py-4 flex items-center gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">
              <T>Zatvoriť okamžite</T>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              <T>Prebije otváracie hodiny na všetky dni, kým to nevypnete.</T>
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.emergencyClosed}
            aria-label={tt('Mimoriadne zatvorené')}
            disabled={!writable}
            data-emergency={form.emergencyClosed ? 'on' : 'off'}
            onClick={() => setForm({ ...form, emergencyClosed: !form.emergencyClosed })}
            className={cn(
              'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
              form.emergencyClosed ? 'bg-red-500' : 'bg-muted',
            )}
          >
            <span
              className={cn(
                'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
                form.emergencyClosed ? 'left-[22px]' : 'left-0.5',
              )}
            />
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-primary" />
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            <T>Oznam o hodinách</T>
          </p>
        </div>
        <div className="px-5 py-4 space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              disabled={!writable}
              checked={form.announcementEnabled}
              onChange={(event) => setForm({ ...form, announcementEnabled: event.target.checked })}
            />
            <T>Zobraziť oznam</T>
          </label>
          <textarea
            aria-label={tt('Text oznamu o hodinách')}
            disabled={!writable}
            rows={2}
            value={form.announcementMessage}
            onChange={(event) => setForm({ ...form, announcementMessage: event.target.value })}
            className="px-3 py-2 rounded-xl bg-background border border-border text-sm w-full resize-none disabled:opacity-60"
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              disabled={!writable}
              checked={form.announcementAlways}
              onChange={(event) => setForm({ ...form, announcementAlways: event.target.checked })}
            />
            <T>Zobrazovať aj počas otváracích hodín</T>
          </label>
          {form.announcementEnabled && form.announcementMessage.trim() === '' && (
            <p className="text-[11px] text-amber-500" data-empty-announcement>
              <T>Oznam je zapnutý, ale nemá text - zákazníkovi sa nezobrazí nič.</T>
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          data-save
          disabled={!writable || save.isPending || broken.length > 0}
          onClick={() => save.mutate({ data: { ...payloadOf(form) } })}
          className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {save.isPending ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť hodiny</T>
        </button>
        {broken.length > 0 && (
          <span className="text-[11px] text-red-500" data-save-blocked>
            {formatCount(broken.length)} <T>dní má neplatné časy - opravte ich pred uložením.</T>
          </span>
        )}
      </div>

      {/* FB-134. The button is useful; what it would change is not obvious, so it is listed. */}
      <ConfirmDialog
        open={confirmReset}
        title={tt('Nastaviť predvolené hodiny?')}
        description={
          pendingReset.length === 0
            ? tt('Hodiny už sú predvolené - nič sa nezmení.')
            : `${tt('Toto prepíše hodiny na predvolené, ktoré nemusia byť hodiny tejto reštaurácie')}: ${pendingReset
                .map((change) => `${dayLabel(change.key)} - ${changeKindLabel(change.kind)} (${change.before} → ${change.after})`)
                .join('; ')}`
        }
        confirmLabel={tt('Prepísať hodiny')}
        variant="danger"
        onClose={() => setConfirmReset(false)}
        onConfirm={() => {
          setForm(resetForm(form))
          setConfirmReset(false)
          toast({
            title: tt('Hodiny prepísané na predvolené'),
            description: tt('Ešte nie sú uložené - skontrolujte ich a uložte.'),
          })
        }}
      />

      {pendingReset.length > 0 && (
        <p className="sr-only" data-reset-change-count>
          {formatCount(pendingReset.length)}
        </p>
      )}

      {broken.length > 0 && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/5 px-4 py-3 text-xs" data-problems>
          <p className="font-bold text-red-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            <T>Neplatné časy</T>
          </p>
          {broken.map((entry) => (
            <p key={entry.key} className="text-muted-foreground">
              <strong>{dayLabel(entry.key)}</strong>: {dayProblemMessage(entry.problem)}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}

const TIME_FIELD =
  'px-2 py-1.5 rounded-lg bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60'
